import { assistantIntents, type AssistantIntent } from '../../lib/assistantIntents';

/**
 * Matching ligero de texto libre contra el catálogo de intents.
 * No es NLU: es un puntaje por coincidencia de tokens (label, descripción
 * y unos sinónimos curados por intent) para que el composer pueda enrutar
 * o prellenar sin depender de frases hardcodeadas como única UX.
 */

// Sinónimos por intent — viven junto al catálogo, no en cada pantalla.
const INTENT_KEYWORDS: Record<string, string[]> = {
  'change-business-hours': ['horario', 'horarios', 'abrir', 'cerrar', 'abierto', 'atencion'],
  'publish-announcement': ['aviso', 'anuncio', 'promocion', 'promo', 'novedad', 'comunicar'],
  'edit-homepage-copy': ['inicio', 'portada', 'texto', 'home', 'titulo', 'descripcion'],
  'check-new-leads': ['consulta', 'consultas', 'cliente', 'clientes', 'mensaje', 'mensajes', 'lead'],
  'mark-leads-read': ['leida', 'leidas', 'leer', 'marcar', 'pendientes', 'revisadas', 'atendidas'],
  'publish-site': ['publicar', 'publica', 'subir', 'lanzar', 'sitio', 'cambios'],
  'create-content-section': ['seccion', 'coleccion', 'contenido', 'crear'],
  'update-whatsapp-message': ['whatsapp', 'wa', 'mensaje', 'contacto'],
};

function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9\s]/g, ' ');
}

function tokensFor(intent: AssistantIntent): Set<string> {
  const base = `${intent.label} ${intent.description}`;
  const extra = INTENT_KEYWORDS[intent.id] ?? [];
  const tokens = normalize(`${base} ${extra.join(' ')}`)
    .split(/\s+/)
    .filter((token) => token.length > 2);
  return new Set(tokens);
}

export type IntentMatch = {
  intent: AssistantIntent;
  score: number;
};

export function matchIntents(
  input: string,
  platform: 'mobile' | 'desktop',
  limit = 4,
): IntentMatch[] {
  const queryTokens = normalize(input).split(/\s+/).filter((token) => token.length > 2);
  if (queryTokens.length === 0) return [];

  return assistantIntents
    .filter((intent) => intent.platforms.includes(platform))
    .map((intent) => {
      const tokens = tokensFor(intent);
      const score = queryTokens.reduce((acc, token) => {
        if (tokens.has(token)) return acc + 2;
        // coincidencia parcial (prefijo) para tolerar conjugaciones simples
        for (const candidate of tokens) {
          if (candidate.startsWith(token) || token.startsWith(candidate)) return acc + 1;
        }
        return acc;
      }, 0);
      return { intent, score };
    })
    .filter((match) => match.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

export function bestIntent(
  input: string,
  platform: 'mobile' | 'desktop',
): AssistantIntent | null {
  return matchIntents(input, platform, 1)[0]?.intent ?? null;
}
